import { LitElement, html, TemplateResult } from 'lit';
import { property, query, state } from 'lit/decorators.js';

import { LocaleCode } from '../../types/lang.types';
import type { SalesSite, SalesSiteDropDownItem } from './types.js';
import { emitEvent, setDerValidity, emitError, emitWarning } from '../../utils/form-associated';
import { msg } from '../../localization/config';

/**
 * Web component for selecting sales sites, integrated with form functionality
 * Fetches sales site data based on language and provides a dropdown for selection
 * Emits custom events on selection change and handles form validation
 */
export class SalesSites extends LitElement {
    static formAssociated = true;
    static override shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true };
    private _internals!: ElementInternals;

    @property({ type: String })
    public lang = LocaleCode.DE_CH;

    @property({ type: String })
    public currency = '';

    @property({ type: String })
    public brand = '';

    @property({ type: String })
    public tenant = '';

    @property({ type: Object, attribute: 'additional-tracking-data' })
    public additionalTrackingData = {};

    @property({ type: String, attribute: 'label' })
    label = '';

    @property({ type: String, attribute: 'name' })
    name = '';

    @property({ type: String, attribute: 'placeholder' })
    placeholder = '';

    @property({ type: Boolean, attribute: 'required' })
    required = false;

    @property({ type: String, attribute: 'selected-team-id' })
    selectedTeamId = '';

    @state()
    private salesSitesData: SalesSite[] = [];

    @query('der-ui-dropdown-searchable') dropdownElement?: HTMLElement & {
        validity?: ValidityState;
        validationMessage?: string;
        reportValidity?: () => boolean;
        checkValidity?: () => boolean;
        reset?: () => void;
        value?: string | null;
    };

    /**
     * Lifecycle method called when the component is added to the DOM
     */
    constructor() {
        super();
        this._internals = this.attachInternals();
        if (!this.hasAttribute('tabindex')) {
            this.tabIndex = 0;
        }
    }

    /**
     * Lifecycle method called when the component is added to the DOM
     */
    override connectedCallback(): void {
        super.connectedCallback();
        void this.getSalesSitesData();
        this.updateComplete.then(() => {
            this.checkInputValidity();
        });
    }

    /**
     * Lifecycle method called when observed properties change
     * @param changedProperties - Map of changed properties and their previous values
     */
    override async updated(changedProperties: Map<string | number | symbol, unknown>): Promise<void> {
        if (changedProperties.has('lang') || changedProperties.has('brand') || changedProperties.has('tenant')) {
            await this.getSalesSitesData();
        }

        if (changedProperties.has('required')) {
            if (!this.required) {
                const hasSelectedSalesSite = !!this.selectedTeamId;
                if (hasSelectedSalesSite) {
                    this.resetSelection();
                }
            }
            this.checkInputValidity();
        }
    }

    resetSelection(): void {
        this.selectedTeamId = '';
        this.setFormValue(null);
        const dropdownEl = this.dropdownElement;
        if (dropdownEl) {
            // Prefer component API to clear visible search input + selection state.
            if (typeof dropdownEl.reset === 'function') {
                dropdownEl.reset();
            } else {
                dropdownEl.value = null;
            }
        }

        this.dispatchEvent(
            new CustomEvent('sales-site-selected', {
                detail: { salesSite: '' },
                bubbles: true,
                composed: true,
            })
        );
    }

    private getSalesSitesRequestUrl(): URL {
        const base = `${import.meta.env.VITE_SALES_SITES_URL ?? ''}`;
        return new URL(base);
    }

    private async getSalesSitesData(): Promise<void> {
        const url = this.getSalesSitesRequestUrl();
        url.searchParams.set('lang', this.lang ?? '');
        url.searchParams.set('brand', this.brand ?? '');
        url.searchParams.set('tenant', this.tenant ?? '');
        url.searchParams.set('teamId', this.selectedTeamId ?? '');
        const response = await fetch(url.toString());

        if (!response.ok) {
            this.error(this.tagName, {
                message: 'Can not load sales-sites data',
            });
            this.salesSitesData = [];
            return;
        }

        const data = (await response.json()) as SalesSite[];
        this.salesSitesData = data;
    }

    /**
     * Get dropdown items formatted for the UI component based on fetched sales site data
     * @returns SalesSiteDropDownItem[] - Array of dropdown items with name and id for selection
     */
    get dropdownItems(): SalesSiteDropDownItem[] {
        if (!Array.isArray(this.salesSitesData) || !this.salesSitesData.length) return [];

        return this.salesSitesData
            .filter((item: SalesSite): boolean => !!item.teamId)
            .map((item: SalesSite): SalesSiteDropDownItem => {
                const { name, address } = item;
                const addr = typeof address === 'object' && address ? address : { zip: '', city: '', street: '' };
                const parts = [addr.zip, addr.city, addr.street, name].filter(Boolean);
                return {
                    name: parts.join(' - '),
                    id: item.teamId,
                };
            });
    }

    private handleChange(event: CustomEvent<SalesSiteDropDownItem | null>): void {
        event.stopPropagation();
        this.selectedTeamId = event.detail?.id ?? '';
        this.setFormValue(this.selectedTeamId || null);

        queueMicrotask(() => {
            this.checkInputValidity();
        });
        this.dispatchEvent(
            new CustomEvent('sales-site-selected', {
                detail: { salesSite: this.selectedTeamId },
                bubbles: true,
                composed: true,
            })
        );
    }

    private handleRendered(): void {
        this.checkInputValidity();
    }

    private checkInputValidity(): void {
        const dropdownEl = this.dropdownElement;
        if (!dropdownEl) return;

        const isValid = dropdownEl.checkValidity?.() ?? true;
        if (isValid) {
            this.setDERValidity({});
            return;
        }

        this.setDERValidity({ customError: true }, dropdownEl.validationMessage || undefined, dropdownEl);
    }

    override focus(): void {
        if (this.dropdownElement && 'focus' in this.dropdownElement) {
            (this.dropdownElement as HTMLElement).focus();
            return;
        }

        super.focus();
    }

    /**
     * Emit a warning event
     * @param name - The warning name
     * @param data - Additional warning data
     */
    protected warn(name: string, data: object): void {
        emitWarning(this, name, data);
    }

    /**
     * Emit an error event
     * @param name - The error name
     * @param data - Additional error data
     */
    protected error(name: string, data: object): void {
        emitError(this, name, data);
    }

    /**
     * Get the ElementInternals instance
     * @returns ElementInternals - The ElementInternals instance associated with this component for form integration
     */
    get internals(): ElementInternals {
        return this._internals;
    }

    /**
     * Set form value
     * @param value - The value to set for the form element
     */
    protected setFormValue(value: string | FormData | File | null): void {
        this._internals.setFormValue(value);
    }

    /**
     * Set validity state and emit validity change event when state changes
     * @param flags - ValidityStateFlags indicating the validity state to set
     * @param message - Optional validation message to associate with the validity state
     * @param anchor - Optional HTMLElement to anchor the validation message to
     */
    setDERValidity(flags?: ValidityStateFlags, message?: string, anchor?: HTMLElement): void {
        setDerValidity(this._internals, this.emitEvent.bind(this), flags, message, anchor, this);
    }

    /**
     * Check validity and report to user
     * @returns boolean - True if the element's value is valid, false otherwise
     */
    reportValidity(): boolean {
        this.checkInputValidity();
        const dropdownEl = this.dropdownElement;
        if (dropdownEl?.reportValidity) {
            return dropdownEl.reportValidity();
        }
        return this._internals.reportValidity();
    }

    /**
     * Check validity without reporting
     * @returns boolean - True if the element's value is valid, false otherwise
     */
    checkValidity(): boolean {
        this.checkInputValidity();
        const dropdownEl = this.dropdownElement;
        if (dropdownEl?.checkValidity) {
            return dropdownEl.checkValidity();
        }
        return this._internals.checkValidity();
    }

    /**
     * Get current validity state
     * @returns ValidityState - The current validity state of the element
     */
    get validity(): ValidityState {
        return this._internals.validity;
    }

    /**
     * Get validation message
     * @returns string - The current validation message of the element, if any
     */
    get validationMessage(): string {
        return this._internals.validationMessage;
    }

    /**
     * Emit a custom event with bubbling and composed enabled
     * @param name - The event name
     * @param detail - The event detail data
     */
    protected emitEvent(name: string, detail: unknown): void {
        emitEvent(this, name, detail);
    }

    /**
     * Render method to define the component's template
     * @returns TemplateResult - The rendered template for the sales site dropdown
     */
    override render(): TemplateResult {
        return html`
            <der-ui-dropdown-searchable
                label="${this.label}"
                name="${this.name}"
                inputPlaceholder="${msg('salesites.inputPlaceholder')}"
                @option-selected=${this.handleChange}
                @rendered=${this.handleRendered}
                .options=${this.dropdownItems}
                .buttonLabel="${this.required ? this.placeholder : ''}"
                lang="${this.lang}"
                ?required="${this.required}"
            ></der-ui-dropdown-searchable>
        `;
    }
}
